/**
 * haiku-grain — the drifting particle canvas.
 *
 * Migrated from github.com/trlmkb/jrl: src/utils/particles.js (Particle,
 * ParticleSystem) plus the setup in src/components/particles.astro, folded
 * into one file. The simulation is unchanged — each particle gets a random
 * position, a life between 1000 and 2000 that ticks down by 10 a frame, a
 * tiny random acceleration each frame, and an alpha that ramps up over the
 * first half of its life and back down over the second. Dead particles are
 * respawned rather than allocated, so the array never grows.
 *
 * WHAT CHANGED IN THE MOVE, and why
 *
 *   · Colour is a parameter. jrl draws rgba(255,255,255,a) because it is a
 *     dark-only site; on this store's paper ground white particles are
 *     invisible. The colour comes from Liquid per appearance mode and is
 *     re-read when html[data-mode] changes.
 *   · Sized to the VIEWPORT, not to <main>. jrl measures main.offsetHeight;
 *     here the canvas is position:fixed, and on the collection page <main> is
 *     a scroll-hijacked 100vh element whose height is not the document's.
 *   · DPR-aware. The original draws in CSS pixels, so the dots are soft on a
 *     retina display. Backing store is scaled and the context pre-scaled.
 *   · Pauses when the tab is hidden and when the document is not visible.
 *     jrl's rAF runs forever.
 *   · Respects prefers-reduced-motion by not starting at all.
 *
 * Fails silently and completely: any throw leaves the two still overlays
 * (noise + wash) doing their job with no canvas at all.
 */
(function () {
  'use strict';

  var cfg = window.haikuGrainConfig || {};
  if (cfg.drift === false) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var canvas = document.querySelector('[data-hgr-drift]');
  if (!canvas || !canvas.getContext) return;

  var ctx = canvas.getContext('2d');
  if (!ctx) return;

  var COUNT = cfg.count || 140;
  /* ⚠️ 1, NOT 2, AND THIS IS THE BIGGEST SINGLE SAVING IN THIS FILE.

     The backing store is the viewport times this. At 2 on a retina desktop a
     1440x900 window means a 2880x1800 canvas — 5.2 MILLION pixels cleared and
     repainted, with 140 arcs filled over them, every frame. At 1 it is 1.3
     million: a quarter of the work for a layer whose entire subject is
     NOISE. Grain is the one thing on a page that gains nothing from being
     drawn at device resolution — it is random dots either way.

     Set `maxDpr: 2` in the snippet to put it back. */
  var MAX_DPR = cfg.maxDpr || 1;

  var w = 0, h = 0, dpr = 1;
  var particles = [];
  var running = false;
  var frameId = 0;

  /* ---- colour ------------------------------------------------------------
     Read from the resolved appearance mode. haiku-modes stamps data-mode on
     <html> before first paint, and this re-reads on every change so the
     particles flip with the rest of the site. */

  var TAU = Math.PI * 2;
  var rgb = '255,255,255';

  /* ⚠️ These MUST be declared before syncColour() runs.
     `var` hoists the declaration but NOT the assignment, and syncColour() is
     called at module top level. When these lived further down the file — next
     to draw(), where they read better — buildStyles() found `fillStyles`
     undefined, threw a TypeError at parse-and-run time, and killed the whole
     script. The canvas then sat at its default 300x150 with nothing ever
     drawn, silently, because the throw happened before the DOMContentLoaded
     listener was attached and the catch in boot() never got a chance to run.
     Caught by counting lit pixels in the canvas, not by reading the code. */
  var BUCKETS = 16;
  var fillStyles = [];
  var buckets = [];

  function buildStyles() {
    fillStyles.length = 0;
    for (var i = 0; i < BUCKETS; i++) {
      // Bucket centre, so no particle is drawn at alpha 0 or clipped at 1.
      fillStyles.push('rgba(' + rgb + ',' + ((i + 0.5) / BUCKETS).toFixed(3) + ')');
    }
  }

  function syncColour() {
    var light = document.documentElement.getAttribute('data-mode') === 'light';
    rgb = light ? (cfg.lightRgb || '20,20,26') : (cfg.darkRgb || '255,255,255');
    buildStyles();
  }

  syncColour();
  try {
    new MutationObserver(syncColour).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-mode'],
    });
  } catch (e) { /* colour just stays as first resolved */ }

  /* ---- the simulation, from jrl's particles.js --------------------------- */

  function spawn(p) {
    p.x = Math.random() * w;
    p.y = Math.random() * h;
    p.vx = 0;
    p.vy = 0;
    p.life = Math.random() * 1000 + 1000;
    p.maxLife = p.life;
    return p;
  }

  function build() {
    particles = [];
    for (var i = 0; i < COUNT; i++) {
      var p = spawn({ accelX: 0, accelY: 0, alpha: 1, size: Math.random() * 1.5 });
      particles.push(p);
    }
  }

  function step() {
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];

      if (p.life < 0) { spawn(p); p.size = Math.random() * 1.5; }

      p.life -= 10;
      p.accelX = (Math.random() - 0.5) * 0.01;
      p.accelY = (Math.random() - 0.5) * 0.01;

      // Ramp up over the first half of life, down over the second.
      p.alpha = p.life >= p.maxLife / 2
        ? 1 - p.life / p.maxLife
        : p.life / p.maxLife;

      p.vx += p.accelX;
      p.vy += p.accelY;
      p.x += p.vx;
      p.y += p.vy;
    }
  }

  /* ---- drawing, batched by alpha ------------------------------------------

     The port originally did what jrl does — per particle, build an
     'rgba(r,g,b,a)' STRING, set fillStyle, beginPath, arc, fill. At 140
     particles that is 140 string allocations and 140 draw calls every frame,
     8400 short-lived strings a second, and the resulting garbage collection
     showed up as periodic stalls: a measured p50 of 16.8ms (a clean 59fps)
     against a p95 of 150ms.

     Instead alpha is quantised into BUCKETS. Particles sharing a bucket share
     one fillStyle and one path, so a frame costs ~16 state changes and ~16
     fills rather than 140 and 140, and the colour strings are built once at
     startup instead of per particle per frame.

     16 buckets is below the threshold where the banding is visible against a
     photograph — the alpha ramp is already slow and the dots are ~1.5px. */

  function draw() {
    ctx.clearRect(0, 0, w, h);

    for (var b = 0; b < BUCKETS; b++) {
      var list = buckets[b];
      if (list) list.length = 0;
      else buckets[b] = [];
    }

    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      var b = (p.alpha * BUCKETS) | 0;
      if (b < 0) b = 0;
      else if (b >= BUCKETS) b = BUCKETS - 1;
      buckets[b].push(p);
    }

    for (var b2 = 0; b2 < BUCKETS; b2++) {
      var group = buckets[b2];
      if (!group.length) continue;

      ctx.fillStyle = fillStyles[b2];
      ctx.beginPath();
      for (var j = 0; j < group.length; j++) {
        var q = group[j];
        // moveTo before arc, or consecutive arcs are joined by a line.
        ctx.moveTo(q.x + q.size, q.y);
        ctx.arc(q.x, q.y, q.size, 0, TAU);
      }
      ctx.fill();
    }
  }

  /* ⚠️ THIS LOOP USED TO RUN FOREVER AT THE DISPLAY'S FULL RATE.

     It repaints a full-viewport canvas — 1440x900 CSS, and the backing store
     is that times devicePixelRatio, so 2880x1800 on a laptop retina screen —
     and it did so 60 times a second for as long as the page was open, on every
     page, whether or not anything else was happening. Reported as the site
     being slow and flickery on desktop.

     Two changes. The drift is now capped at FPS: grain moving at 30fps is
     indistinguishable from grain moving at 60, and it halves the work. And a
     hidden document no longer SCHEDULES a frame — it used to skip the drawing
     but keep the loop alive, which still costs a callback every frame; now the
     loop ends and visibilitychange restarts it. */
  var FPS = typeof cfg.fps === 'number' ? cfg.fps : 30;
  var minDelta = 1000 / FPS;
  var lastFrame = 0;

  function tick(now) {
    if (document.visibilityState === 'hidden') { frameId = 0; return; }
    frameId = requestAnimationFrame(tick);
    var t = now || performance.now();
    if (t - lastFrame < minDelta) return;
    lastFrame = t;
    step();
    draw();
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'hidden' && !frameId) {
      lastFrame = 0;
      frameId = requestAnimationFrame(tick);
    }
  });

  /* ---- sizing ------------------------------------------------------------ */

  function resize() {
    w = window.innerWidth;
    h = window.innerHeight;
    dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);

    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);

    // Draw in CSS pixels; the backing store carries the extra resolution.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function boot() {
    try {
      resize();
      build();
      if (!running) { running = true; tick(); }
    } catch (e) {
      cancelAnimationFrame(frameId);
      canvas.remove();
    }
  }

  var resizeId;
  window.addEventListener('resize', function () {
    clearTimeout(resizeId);
    // Rebuild rather than rescale: particles carry absolute positions, and a
    // rescale would leave them clustered where the old viewport was.
    resizeId = setTimeout(boot, 200);
  });

  /* After load, at the next idle moment — not at DOMContentLoaded. Every
     particle is born at alpha 0 and ramps in over its first 1–2 s anyway, so
     the field looks the same; what moves is ~2 s of main-thread drawing that
     used to land while the first photograph was still waiting to paint
     (Lighthouse mobile, homepage, 2026-10-25). */
  function start() {
    if (window.requestIdleCallback) window.requestIdleCallback(boot, { timeout: 1500 });
    else setTimeout(boot, 200);
  }

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start);
}());
